import { FC, useEffect, useRef, useState } from 'react';
import {
  MessageCircle,
  MapPin,
  Building,
  Calendar,
  ShoppingBag,
  UserCheck,
  Ruler,
  Construction,
  ChevronsRight,
  Plus
} from 'lucide-react';
import { Customer } from '../../lib/types';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { resolveUserName } from '../../lib/directus';
import { CustomerActivityModal } from './CustomerActivityModal';
import { CustomerInteractionModal } from './CustomerInteractionModal';
import { useAppSelector } from '../../hooks/redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Badge } from './badge';
import { Avatar, AvatarFallback } from './avatar';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';

interface CustomerDetailsModalProps {
  customer: Customer;
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerDetailsModal: FC<CustomerDetailsModalProps> = ({
  customer,
  isOpen,
  onClose
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [assignedPicName, setAssignedPicName] = useState<string>('');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const { t, language } = useLanguage();

  // Get current user ID from auth state
  const currentUser = useAppSelector((state) => state.auth.employee);

  // Auto-scroll to top when modal opens
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Resolve assigned PIC name
  useEffect(() => {
    if (customer.assigned_pic) {
      resolveUserName(customer.assigned_pic)
        .then(name => setAssignedPicName(name))
        .catch(error => {
          console.warn('Failed to resolve PIC name:', error);
          setAssignedPicName(customer.assigned_pic); // Fallback to original value
        });
    } else {
      setAssignedPicName('');
    }
  }, [customer.assigned_pic]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'inactive':
        return 'bg-destructive/10 text-destructive';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
      default:
        return 'bg-muted/20 text-muted-foreground';
    }
  };

  // Helper function to format dates with dynamic locale
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return as-is if parsing fails
    }
  };

  // Helper function to calculate farm age
  const calculateFarmAge = (yearBuilt?: number) => {
    if (!yearBuilt) return null;
    const currentYear = new Date().getFullYear();
    const age = currentYear - yearBuilt;
    return age > 0 ? t('customer_details_modal.farm_age', { age }) : t('customer_details_modal.recently_built');
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              <Avatar className="w-12 h-12 bg-primary/10">
                <AvatarFallback className="bg-primary/10">
                  <Building className="w-6 h-6 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-lg font-semibold truncate">
                  {customer.tambak_name || customer.name || customer.NamaPelangganTambak}
                </DialogTitle>
                <p className="text-sm text-muted-foreground truncate">{t('customer_details_modal.title')}</p>
              </div>
            </div>
          </DialogHeader>

        {/* Approval Status Warnings - Moved to top for visibility */}
        {/* {customer.approval_status === 'pending' && (
          <div className="mx-4 sm:mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700 font-medium">⚠️ Customer pending approval</p>
            <p className="text-xs text-amber-600">Contact management before scheduling visits</p>
          </div>
        )}
        
        {customer.approval_status === 'rejected' && (
          <div className="mx-4 sm:mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-700 font-medium">❌ Customer rejected</p>
            <p className="text-xs text-red-600">This customer cannot be used for sales activities</p>
          </div>
        )} */}

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          className="overflow-y-auto flex-1 min-h-0 max-h-full space-y-6 px-1"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--border)) hsl(var(--background))'
          }}
        >
          <div className="space-y-6">
            {/* Customer Overview Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  {t('customer_details_modal.overview')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('customer_details_modal.customer_id')}</span>
                  <span className="font-medium">{customer.idcust}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('customer_details_modal.approval_status')}</span>
                  <ApprovalStatusBadge
                    status={customer.approval_status || 'approved'}
                    size="md"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('customer_details_modal.status')}</span>
                  <Badge
                    variant="secondary"
                    className={cn(getStatusColor(customer.status || 'active'))}
                  >
                    {t(`customer_details_modal.status_${customer.status || 'active'}`)}
                  </Badge>
                </div>

                {/* Customer Type Information */}
                {customer.tipe_pelanggan && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('customer_details_modal.customer_type')}</span>
                    <Badge variant="secondary" className="bg-purple-500/10 text-purple-600 dark:text-purple-400">
                      {customer.tipe_pelanggan}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Farm Information Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="w-4 h-4 text-primary" />
                  {t('customer_details_modal.farm_information')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {customer.tambak_size && (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <Ruler className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{t('customer_details_modal.size')}</span>
                      </div>
                      <p className="text-sm font-medium truncate">{customer.tambak_size}</p>
                    </div>
                  )}

                  {customer.year_built && (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <Construction className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{t('customer_details_modal.built')}</span>
                      </div>
                      <p className="text-sm font-medium">{customer.year_built}</p>
                      <p className="text-xs text-muted-foreground">{calculateFarmAge(customer.year_built)}</p>
                    </div>
                  )}
                </div>

                {customer.lead_source && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-1">
                      <UserCheck className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{t('customer_details_modal.lead_source')}</span>
                    </div>
                    <p className="text-sm font-medium">{customer.lead_source}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Address & Contact Card */}
            {(customer.address || customer.Kota || customer.Provinsi || customer.phone || customer.Contacs) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {t('customer_details_modal.location_contact')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(customer.address || customer.Kota || customer.Provinsi) && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">{t('customer_details_modal.address')}</span>
                      <p className="text-sm text-foreground">
                        {[customer.address, customer.Kota, customer.Provinsi].filter(Boolean).join(' - ')}
                      </p>
                      {customer.distance && (
                        <p className="text-xs text-muted-foreground">📍 {customer.distance}</p>
                      )}
                    </div>
                  )}

                  {(customer.phone || customer.Contacs) && (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">{t('customer_details_modal.contact_information')}</span>
                      <div className="space-y-1">
                        {customer.phone && (
                          <p className="text-sm text-foreground">📞 {customer.phone}</p>
                        )}
                        {customer.Contacs && customer.Contacs !== customer.phone && (
                          <p className="text-sm text-foreground">📱 {customer.Contacs}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Activity Information Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-green-600 dark:text-green-400" />
                  {t('customer_details_modal.activity_information')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {customer.last_purchase_date && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ShoppingBag className="w-3 h-3 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{t('customer_details_modal.last_purchase')}</span>
                    </div>
                    <span className="text-sm font-medium">{formatDate(customer.last_purchase_date)}</span>
                  </div>
                )}

                {customer.lastInteraction && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="w-3 h-3 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{t('customer_details_modal.last_interaction')}</span>
                    </div>
                    <span className="text-sm font-medium">
                      {customer.lastInteraction === 'Belum ada' || customer.lastInteraction === 'No interactions yet'
                        ? t('customer_details_modal.no_interaction')
                        : customer.lastInteraction}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Management Card */}
            {customer.assigned_pic && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    {t('customer_details_modal.account_management')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('customer_details_modal.assigned_pic')}</span>
                    <span className="text-sm font-medium">
                      {assignedPicName || customer.assigned_pic || t('customer_details_modal.not_assigned')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

            {/* Actions Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('customer_details_modal.actions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground text-center">
                  {t('customer_details_modal.create_interaction_prompt')}
                </div>

                <Button
                  onClick={() => setShowInteractionModal(true)}
                  disabled={customer.approval_status === 'rejected'}
                  className="w-full"
                  size="lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('customer_details_modal.create_interaction')}
                </Button>

                <Button
                  onClick={() => setShowActivityModal(true)}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <ChevronsRight className="w-4 h-4 mr-2" />
                  {t('customer_details_modal.view_activity')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Child Modals - rendered outside main dialog */}
      {showActivityModal && (
        <CustomerActivityModal
          customer={customer}
          isOpen={showActivityModal}
          onClose={() => setShowActivityModal(false)}
          currentUserId={currentUser?.id}
        />
      )}

      {showInteractionModal && (
        <CustomerInteractionModal
          customer={customer}
          isOpen={showInteractionModal}
          onClose={() => setShowInteractionModal(false)}
          showDateTimePicker={true}
          simplifiedMode={true}
        />
      )}
    </>
  );
};